import React from 'react';
import { motion } from 'framer-motion';
import { Search, Code2, TestTube, Rocket } from 'lucide-react';

const steps = [
  { id: '01', title: 'Business Strategy & Plan', icon: <Search size={24} />, desc: 'We deep-dive into your operational challenges to map out a clear technology strategy that directly supports your business goals.' },
  { id: '02', title: 'Iterative Engineering', icon: <Code2 size={24} />, desc: 'Our experienced engineers build secure, high-quality, and scalable software with complete transparency at every milestone.' },
  { id: '03', title: 'Rigorous Verification', icon: <TestTube size={24} />, desc: 'We conduct exhaustive testing to guarantee a stable, secure, and reliable system that your business and customers can fully trust.' },
  { id: '04', title: 'Deploy & Grow', icon: <Rocket size={24} />, desc: 'We ensure a seamless deployment followed by continuous support and optimization, keeping your systems aligned with your growth.' }
];

const Workflow = () => {
  return (
    <section className="py-24 bg-slate-50/40 backdrop-blur-md relative border-y border-slate-200/50">
      <div className="container mx-auto px-6 max-w-7xl">
        <div className="text-center max-w-2xl mx-auto mb-20">
          <motion.div 
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-block px-4 py-2 rounded-full bg-white border border-slate-200 text-slate-600 font-semibold text-sm mb-6 shadow-sm"
          >
            Our Proven Process
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-4xl md:text-5xl font-bold text-slate-900 mb-6"
          >
            Our Path to <span className="text-blue-600">Your Business Growth</span>
          </motion.h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 relative">
          {/* Connector Line for Desktop */}
          <div className="hidden lg:block absolute top-[4.5rem] left-[10%] right-[10%] h-[2px] bg-gradient-to-r from-blue-100 via-blue-300 to-blue-100 z-0"></div>

          {steps.map((step, index) => (
            <motion.div 
              key={step.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.15, type: 'spring', stiffness: 50 }}
              className="relative z-10 flex flex-col items-center text-center group"
            >
              <div className="w-36 h-36 relative mb-8 flex items-center justify-center">
                <div className="absolute inset-0 bg-white rounded-full shadow-lg border-4 border-slate-50 group-hover:border-blue-100 transition-colors duration-500"></div>
                <div className="w-20 h-20 bg-slate-50 rounded-full flex items-center justify-center text-slate-700 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-500 shadow-inner z-10">
                  {step.icon}
                </div>
                <div className="absolute -top-3 -right-3 w-10 h-10 bg-slate-900 text-white rounded-full flex items-center justify-center font-bold border-4 border-white shadow-sm">
                  {step.id}
                </div>
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-3">{step.title}</h3>
              <p className="text-slate-600 leading-relaxed">{step.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Workflow;
